import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { environment } from "../../environments/environment";
import { Observable, of } from "rxjs";
declare global {
	interface Window {
		grecaptcha: any;
		onRecaptchaLoaded: () => void;
	}
}

@Injectable({
	providedIn: "root",
})
export class RecaptchaService {
	private isBrowser: boolean;
	private recaptchaLoaded = false;
	private loadingPromise: Promise<void> | null = null;

	constructor() {
		const platformId = inject(PLATFORM_ID);

		this.isBrowser = isPlatformBrowser(platformId);
	}

	/**
	 * Load the reCAPTCHA script
	 */
	public loadRecaptchaScript(): Promise<void> {
		if (this.loadingPromise) {
			return this.loadingPromise;
		}

		this.loadingPromise = new Promise((resolve, reject) => {
			if (!this.isBrowser) {
				resolve();
				return;
			}

			if (this.recaptchaLoaded || typeof window.grecaptcha !== "undefined") {
				this.recaptchaLoaded = true;
				resolve();
				return;
			}

			// Set up the callback function
			window.onRecaptchaLoaded = (): void => {
				this.recaptchaLoaded = true;
				resolve();
			};

			const script = document.createElement("script");
			// Use Enterprise API as per Google documentation
			script.src = `https://www.google.com/recaptcha/enterprise.js?render=${environment.recaptcha.siteKey}`;
			script.async = true;
			script.defer = true;

			script.onload = (): void => {
				// For Enterprise with render parameter, we don't use the callback
				this.recaptchaLoaded = true;
				resolve();
			};

			script.onerror = (error): void => {
				this.loadingPromise = null;
				reject(`Error loading reCAPTCHA: ${error}`);
			};

			document.head.appendChild(script);
		});

		return this.loadingPromise;
	}

	/**
	 * Execute reCAPTCHA Enterprise
	 * @param action The action to execute
	 * @returns Promise with the token
	 */
	public executeRecaptcha(action: string): Promise<string> {
		if (!this.isBrowser) {
			return Promise.resolve("");
		}

		return this.loadRecaptchaScript()
			.then(() => {
				return new Promise<string>((resolve, reject) => {
					// For Enterprise API, we need to wait for the script to load and initialize
					const executeWhenReady = (): void => {
						if (window.grecaptcha?.enterprise?.execute) {
							window.grecaptcha.enterprise
								.execute(environment.recaptcha.siteKey, { action })
								.then((token: string) => {
									resolve(token);
								})
								.catch((error: any) => {
									reject(error);
								});
						} else if (window.grecaptcha?.execute) {
							window.grecaptcha
								.execute(environment.recaptcha.siteKey, { action })
								.then((token: string) => {
									resolve(token);
								})
								.catch((error: any) => {
									reject(error);
								});
						} else {
							reject(new Error("No reCAPTCHA execute method available"));
						}
					};

					// Check if grecaptcha.ready is available (standard API)
					if (window.grecaptcha?.ready) {
						window.grecaptcha.ready(executeWhenReady);
					} else {
						// For Enterprise API without ready method, we need to wait a bit
						setTimeout(() => {
							if (window.grecaptcha?.enterprise?.execute) {
								executeWhenReady();
							} else {
								// Wait a bit more for the Enterprise API to load
								setTimeout(() => {
									if (window.grecaptcha?.enterprise?.execute) {
										executeWhenReady();
									} else {
										reject(new Error("reCAPTCHA Enterprise API not available"));
									}
								}, 500);
							}
						}, 100);
					}
				});
			})
			.catch(() => {
				return "";
			});
	}

	/**
	 * Get reCAPTCHA Enterprise token
	 * @param action The action to execute
	 * @returns Promise with the token
	 */
	public getToken(action: string): Promise<string> {
		return this.executeRecaptcha(action);
	}

	/**
	 * Render a reCAPTCHA v2 widget to the specified container (Enterprise mode returns -1)
	 * @param container The DOM element or ID where the reCAPTCHA should be rendered
	 * @param options Optional reCAPTCHA options
	 * @returns The ID of the reCAPTCHA widget
	 */
	public renderRecaptcha(
		_container: string | HTMLElement,
		_options: any = {},
	): Observable<number> {
		// Enterprise reCAPTCHA doesn't need visual widget
		return of(-1);
	}

	/**
	 * Check if the reCAPTCHA has been verified (Enterprise mode returns empty string)
	 * @param widgetId The ID of the reCAPTCHA widget to check
	 * @returns True if the reCAPTCHA has been verified
	 */
	public getResponse(_widgetId: number): string {
		// Enterprise reCAPTCHA doesn't use widget responses
		return "";
	}

	/**
	 * Reset the reCAPTCHA widget
	 * @param widgetId The ID of the reCAPTCHA widget to reset
	 */
	public resetRecaptcha(widgetId: number): void {
		if (!this.isBrowser || widgetId === -1) {
			return;
		}

		try {
			window.grecaptcha.reset(widgetId);
		} catch {
			// Silently handle reset errors
		}
	}
}
